"use client";

import type { ReactNode } from "react";
import { Gift } from "lucide-react";
import { cn } from "@/lib/utils";
import { SportsbookLogoMark } from "@/components/sportsbook-logos";
import { sportsbookIdToDisplay } from "@/lib/sportsbook-registry";
import { useBookAvailability } from "@/contexts/book-availability-context";
import { NOT_BETTABLE_CLASS, notBettableIn, notHere } from "@/lib/book-location";
import { useGameLadder } from "@/hooks/use-odds-live";
import type { LadderLineSummary } from "@/lib/odds-screen/live/derive";
import type { BookId, EventHeader, FamilySummary, MarketSpec, OddsDisplay, StakeKind } from "@/lib/odds-screen/live/types";
import { railModel, type DrillTarget, type RailBookRow, type RailColumn, type RailModel } from "./drill";
import { formatStake, matchupLabel, signedLine, trimLine } from "./labels";
import { rowSpec, subjectKind } from "./subject";
import { marketDisplayOrder } from "./view-model";
import { KICKOFF_TIME, clock, metaDate, startedAt } from "./time";
import { LatestPlay, LiveState } from "./live-state";
import { LineHistoryRow } from "./line-history-row";
import { ExternalArrow } from "./price-cell";
import { OPENER_BOOK, hasOpeners, openPrices, useOpeners, type OpenPrice } from "./openers";
import { betHref, betTarget, signupOffer, type BetTarget } from "./bet";
import type { OpenOddsHistory } from "@/components/odds/odds-history-selection";

export type BookStake = ReadonlyMap<BookId, StakeKind | null>;
export type RailLinkClick = (target: BetTarget, decimal: number | null, best: boolean) => void;
export type HasAccount = (book: BookId) => boolean;

const ROW_GRID = { gridTemplateColumns: "20px minmax(0,1fr) auto" } as const;

function BookLinkCue({ target }: { target: BetTarget | null }) {
  return (
    <span aria-hidden="true" className="flex w-3 flex-shrink-0 justify-end">
      {target?.kind === "bet" && <ExternalArrow className="text-zinc-500 transition-colors duration-100 [@media(hover:hover)]:group-hover/book:text-primary" />}
      {target?.kind === "signup" && <Gift size={10} strokeWidth={2.5} className="text-accent-value" />}
    </span>
  );
}

function linkLabel(target: BetTarget | null): string {
  if (target === null) return "";
  const name = sportsbookIdToDisplay(target.book);
  return target.kind === "bet" ? `, opens ${name}` : `, opens the ${name} sign-up offer`;
}

function RailBookRowView({
  row, outcomeLabel, bookStake, hasAccount, onLinkClick, rowClassName, isMobile,
}: {
  row: RailBookRow;
  outcomeLabel: string;
  bookStake: BookStake;
  hasAccount: HasAccount;
  onLinkClick: RailLinkClick;
  rowClassName: string;
  isMobile: boolean;
}) {
  const { place } = useBookAvailability();
  const away = notHere(place, row.bettable);
  const stake = formatStake(row.stake, bookStake.get(row.book) ?? null);
  const target = row.bettable ? betTarget(row.book, betHref(row, isMobile), hasAccount(row.book)) : null;
  const offer = target?.kind === "signup" ? signupOffer(row.book) : null;
  const inner = (
    <>
      <SportsbookLogoMark sportsbook={row.book} size={20} decorative />
      {offer && stake === null && !row.held ? (
        <span className="data-num min-w-0 truncate text-[10px] font-semibold text-accent-value">{offer.ctaAmount} offer</span>
      ) : (
        <span className="data-num min-w-0 truncate text-[10px] text-zinc-500">{away ? `Not in ${away}` : row.held ? "Paused" : stake?.text}</span>
      )}
      <span className="flex flex-shrink-0 items-center gap-1.5">
        <span className={cn("lp data-num text-right text-[13px] font-semibold", row.held ? "text-zinc-500" : row.best ? "text-accent-value" : "text-foreground")}>{row.display}</span>
        <BookLinkCue target={target} />
      </span>
    </>
  );
  const className = cn(
    "group/book grid items-center gap-2 rounded-md px-2 py-1 transition-colors duration-100 hover:bg-white/[0.05]",
    rowClassName,
    row.best && "bg-accent-value/[0.08] hover:bg-accent-value/[0.12]",
    away && NOT_BETTABLE_CLASS,
    row.belowMin && "opacity-40",
  );
  const quote = `${outcomeLabel}: ${sportsbookIdToDisplay(row.book)} ${row.display}${row.held ? ", paused" : ""}${stake ? `, ${stake.text}` : ""}${row.belowMin ? ", below your minimum" : ""}`;
  const label = away ? notBettableIn(quote, away) : `${quote}${linkLabel(target)}`;
  return target ? (
    <a href={target.href} target="_blank" rel="noopener noreferrer" aria-label={label} title={offer ? offer.details : undefined} className={className} style={ROW_GRID} onClick={() => onLinkClick(target, row.decimal, row.best)}>{inner}</a>
  ) : (
    <div aria-label={label} title={away ? notBettableIn(sportsbookIdToDisplay(row.book), away) : undefined} className={className} style={ROW_GRID}>{inner}</div>
  );
}

function RailOtherRowView({ other, hasAccount, onLinkClick, rowClassName, isMobile }: { other: RailModel["columns"][number]["others"][number]; hasAccount: HasAccount; onLinkClick: RailLinkClick; rowClassName: string; isMobile: boolean }) {
  const { place } = useBookAvailability();
  const away = notHere(place, other.bettable);
  const target = other.bettable ? betTarget(other.book, betHref(other, isMobile), hasAccount(other.book)) : null;
  const inner = (
    <>
      <SportsbookLogoMark sportsbook={other.book} size={20} decorative />
      {away ? <span className="data-num min-w-0 truncate text-[10px] text-zinc-500">Not in {away}</span> : <span />}
      <span className="flex flex-shrink-0 items-center gap-1.5">
        <span className="data-num flex items-baseline gap-1.5 text-[13px] font-semibold text-foreground"><span className="text-[11px] font-medium text-zinc-400">{other.lineLabel}</span>{other.display}</span>
        <BookLinkCue target={target} />
      </span>
    </>
  );
  const className = cn("group/book grid items-center gap-2 rounded-md px-2 py-1 transition-colors duration-100 hover:bg-white/[0.05]", away && NOT_BETTABLE_CLASS, other.belowMin ? "opacity-40" : !away && "opacity-75", rowClassName);
  const quote = `${sportsbookIdToDisplay(other.book)} ${other.lineLabel} ${other.display}${other.belowMin ? ", below your minimum" : ""}`;
  const label = away ? notBettableIn(quote, away) : `${quote}${linkLabel(target)}`;
  return target ? (
    <a href={target.href} target="_blank" rel="noopener noreferrer" aria-label={label} className={className} style={ROW_GRID} onClick={() => onLinkClick(target, null, false)}>{inner}</a>
  ) : (
    <div aria-label={label} title={away ? notBettableIn(sportsbookIdToDisplay(other.book), away) : undefined} className={className} style={ROW_GRID}>{inner}</div>
  );
}

function OpenLine({ open }: { open: OpenPrice }) {
  return (
    <span title="Pinnacle's first price for this market" className="data-num flex w-full items-center gap-1 whitespace-nowrap text-[10px] leading-[14px] text-zinc-500">
      <SportsbookLogoMark sportsbook={OPENER_BOOK} size={12} decorative />
      Open{open.line && ` ${open.line}`} <span className="font-semibold text-zinc-400">{open.display}</span>
    </span>
  );
}

function RailColumnView({
  column, open, stacked, highlighted, bookStake, hasAccount, onLinkClick, rowClassName, isMobile,
}: {
  column: RailColumn;
  open: OpenPrice | null;
  stacked: boolean;
  highlighted: boolean;
  bookStake: BookStake;
  hasAccount: HasAccount;
  onLinkClick: RailLinkClick;
  rowClassName: string;
  isMobile: boolean;
}) {
  return (
    <div className="row-span-2 grid min-w-0 grid-rows-subgrid">
      <div className={stacked ? "flex min-h-[46px] flex-col justify-end gap-0.5 border-b border-surface-line px-1 pb-1.5" : open ? "flex min-h-[38px] flex-wrap content-end items-end justify-between gap-x-2 gap-y-0.5 border-b border-surface-line px-1 pb-1.5" : "flex min-h-[38px] items-end justify-between gap-2 border-b border-surface-line px-1 pb-1.5"}>
        <span title={column.label} className={cn("line-clamp-2 break-words text-[12px] font-semibold leading-[14px]", !stacked && open && "min-w-0 flex-1", highlighted ? "text-primary" : "text-foreground")}>{column.label}</span>
        <span className="data-num flex-shrink-0 text-[10px] leading-[14px] text-zinc-500">{column.rows.length} {column.rows.length === 1 ? "book" : "books"}</span>
        {open && <OpenLine open={open} />}
      </div>
      <div className="flex flex-col gap-1">
        <div className="flex flex-col gap-0.5">
          {column.rows.length === 0 ? (
            <span className="px-1 py-3 text-[11px] text-zinc-500">No books</span>
          ) : (
            column.rows.map((row) => (
              <RailBookRowView key={row.book} row={row} outcomeLabel={column.label} bookStake={bookStake} hasAccount={hasAccount} onLinkClick={onLinkClick} rowClassName={rowClassName} isMobile={isMobile} />
            ))
          )}
        </div>
        {column.others.length > 0 && (
          <div className="mt-2 flex flex-col gap-0.5">
            <span className="px-1 pb-0.5 text-[10px] font-medium uppercase tracking-[0.06em] text-zinc-500">Other lines</span>
            {column.others.map((other) => (
              <RailOtherRowView key={other.book} other={other} hasAccount={hasAccount} onLinkClick={onLinkClick} rowClassName={rowClassName} isMobile={isMobile} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function RailColumns({
  columns, opens, bookStake, highlightOutcome, hasAccount, onLinkClick, isMobile, rowClassName = "min-h-[34px]",
}: {
  columns: readonly RailColumn[];
  opens?: readonly (OpenPrice | null)[];
  bookStake: BookStake;
  highlightOutcome: number;
  hasAccount: HasAccount;
  onLinkClick: RailLinkClick;
  isMobile: boolean;
  rowClassName?: string;
}) {
  return (
    <div className="grid gap-x-3 gap-y-1" style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(0,1fr))` }}>
      {columns.map((column, index) => (
        <RailColumnView
          key={column.outcome}
          column={column}
          open={opens?.[index] ?? null}
          stacked={columns.length > 2}
          highlighted={column.outcome === highlightOutcome}
          bookStake={bookStake}
          hasAccount={hasAccount}
          onLinkClick={onLinkClick}
          rowClassName={rowClassName}
          isMobile={isMobile}
        />
      ))}
    </div>
  );
}

const LINE_STEP = "flex w-7 flex-shrink-0 items-center justify-center text-zinc-400 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40 disabled:opacity-35 disabled:hover:text-zinc-400";

export function LineTabs({
  summaries, spec, outcome, lineValue, touch = false, onNavigate,
}: {
  summaries: readonly LadderLineSummary[];
  spec: MarketSpec;
  outcome: number;
  lineValue: number | null;
  touch?: boolean;
  onNavigate: (patch: Partial<DrillTarget>) => void;
}) {
  const currentIndex = summaries.findIndex((entry) => entry.line === lineValue);
  const start = Math.max(0, Math.min(currentIndex - 2, summaries.length - 5));
  const tabs = summaries.slice(start, start + 5);
  const previous = currentIndex > 0 ? summaries[currentIndex - 1] : undefined;
  const next = currentIndex >= 0 ? summaries[currentIndex + 1] : undefined;

  return (
    <div className={cn("flex flex-shrink-0 items-stretch border-b border-surface-line", touch ? "h-12 px-4" : "h-11 px-2")}>
      {!touch && (
        <button type="button" aria-label="Previous line" disabled={previous === undefined} onClick={() => previous && onNavigate({ line: previous.line })} className={LINE_STEP}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m15 18-6-6 6-6" /></svg>
        </button>
      )}
      {tabs.map((tab) => {
        const label = spec.line === "spread" ? signedLine(spec.outcomes[outcome] === "away" ? -tab.line : tab.line) : trimLine(tab.line);
        return (
          <button
            key={tab.line}
            type="button"
            aria-pressed={tab.line === lineValue}
            aria-label={`${label}, ${tab.books} books`}
            onClick={() => onNavigate({ line: tab.line })}
            className={cn(
              "flex min-w-0 flex-1 flex-col items-center justify-center gap-px transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40",
              tab.line === lineValue ? "text-foreground shadow-[inset_0_-2px_0_#00d4ff]" : "text-zinc-400 shadow-[inset_0_-2px_0_transparent] hover:text-foreground",
            )}
          >
            <span className={cn("data-num font-semibold", touch ? "text-[13px]" : "text-[12px]")}>{label}</span>
            <span className="data-num text-[10px] font-medium text-zinc-500">{tab.books}{tab.isMain ? " · main" : ""}</span>
          </button>
        );
      })}
      {!touch && (
        <button type="button" aria-label="Next line" disabled={next === undefined} onClick={() => next && onNavigate({ line: next.line })} className={LINE_STEP}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m9 18 6-6-6-6" /></svg>
        </button>
      )}
    </div>
  );
}

export function useRailModel(target: DrillTarget, family: FamilySummary, event: EventHeader | undefined, selected: ReadonlySet<BookId>, listed: ReadonlySet<BookId>, format: OddsDisplay, minStake: number, entityName: string | null) {
  const ladder = useGameLadder({ league: target.league, event: target.event, family: target.family, entity: target.entity }, event, family.markets);
  const spec = rowSpec(family, entityName === null ? undefined : { entity: { key: target.entity, name: entityName } }, target.slot);
  const model: RailModel | null = ladder ? railModel(ladder, spec, target.slot, target.line, event, selected, listed, format, minStake) : null;
  const modelAt = (line: number): RailModel | null => (ladder ? railModel(ladder, spec, target.slot, line, event, selected, listed, format, minStake) : null);
  const opensShown = hasOpeners(target.family);
  const openers = useOpeners(target.league, opensShown ? [target.event] : []);
  const opens = model === null || !opensShown
    ? undefined
    : openPrices(openers.get(target.event), spec, model.lineValue, model.columns.map((column) => column.outcome), format).map((open) => (open === "none" ? null : open));
  return { spec, model, modelAt, opens, loading: ladder === null };
}

export function DrillRail({
  target, family, event, entityName, avatar, selected, listed, format, minStake, isMobile, bookStake, hasAccount, animate = true, onClose, onNavigate, onHide, onLinkClick, onOpenHistory, openHistoryFor,
}: {
  target: DrillTarget;
  family: FamilySummary;
  event: EventHeader | undefined;
  entityName: string | null;
  avatar: ReactNode;
  selected: ReadonlySet<BookId>;
  listed: ReadonlySet<BookId>;
  format: OddsDisplay;
  minStake: number;
  isMobile: boolean;
  bookStake: BookStake;
  hasAccount: HasAccount;
  animate?: boolean;
  onClose: () => void;
  onNavigate: (patch: Partial<DrillTarget>) => void;
  onHide: () => void;
  onLinkClick: RailLinkClick;
  onOpenHistory: OpenOddsHistory;
  openHistoryFor?: { book: BookId; onOpened: () => void };
}) {
  const specs = family.markets;
  const { spec, model, modelAt, opens, loading } = useRailModel(target, family, event, selected, listed, format, minStake, entityName);
  const isProp = family.kind === "props";
  const subject = subjectKind(family, entityName !== null);
  const matchup = matchupLabel(event, target.league);
  const title = entityName ?? matchup;
  const order = marketDisplayOrder(specs);

  const summaries = model?.summaries ?? [];

  return (
    <aside
      aria-label={`Compare books for ${title}`}
      onKeyDown={(e) => { if ((e.key === "h" || e.key === "H") && !e.metaKey && !e.ctrlKey) { e.preventDefault(); onHide(); } }}
      className={cn("flex h-full w-[380px] max-w-[calc(100vw-16px)] flex-shrink-0 flex-col border-l border-surface-line bg-surface-raised shadow-[-24px_0_48px_-24px_rgba(0,0,0,0.6)]", animate && "duration-200 animate-in fade-in-0 slide-in-from-right-4 motion-reduce:animate-none")}
    >
      <div className="flex items-start gap-3 py-3 pl-4 pr-3">
        {avatar}
        <div className="flex min-w-0 flex-grow flex-col gap-1">
          {(isProp || specs.length <= 1) && <span className="text-[10px] font-medium uppercase tracking-[0.14em] text-zinc-500">{spec.label}</span>}
          <h2 className="break-words text-[16px] font-semibold leading-[22px] text-foreground" style={{ fontFamily: "'Plus Jakarta Sans', Inter, system-ui, sans-serif" }}>{title}</h2>
          {!isProp && entityName !== null && <span className="truncate text-[12px] text-zinc-400">{matchup}</span>}
          {event && (event.live
            ? <><LiveState live={event.live} className="self-start" /><LatestPlay play={event.live.plays[0] ?? null} /><span className="data-num truncate text-[12px] text-zinc-500">Started {startedAt(event.startMs)}</span></>
            : <span className="data-num truncate text-[12px] text-zinc-500">{metaDate(event.startMs)} · <span className={KICKOFF_TIME}>{clock(event.startMs)}</span></span>)}
        </div>
        <button
          type="button"
          onClick={onHide}
          aria-keyshortcuts="H"
          aria-label={subject === "game" ? `Hide ${matchupLabel(event, target.league, "spoken") || "game"} from this board` : `Hide ${entityName ?? subject} from this board`}
          title={`Hide ${subject}`}
          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-zinc-400 hover:bg-white/[0.06] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49" /><path d="M14.084 14.158a3 3 0 0 1-4.242-4.242" /><path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143" /><path d="m2 2 20 20" /></svg>
        </button>
        <button type="button" onClick={onClose} aria-label="Close comparison" className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-zinc-400 hover:bg-white/[0.06] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
        </button>
      </div>

      {!isProp && specs.length > 1 ? (
        <div role="tablist" aria-label="Markets for this game" className="flex h-9 flex-shrink-0 items-stretch gap-4 border-b border-surface-line px-4">
          {order.map((slot) => (
            <button key={specs[slot].id} type="button" role="tab" aria-selected={slot === target.slot} onClick={() => onNavigate({ slot, line: null })} className={cn("text-[13px] font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40", slot === target.slot ? "text-foreground shadow-[inset_0_-2px_0_#00d4ff]" : "text-zinc-400 shadow-[inset_0_-2px_0_transparent] hover:text-foreground")}>{specs[slot].label}</button>
          ))}
        </div>
      ) : (
        <div className="h-px bg-surface-line" />
      )}

      {summaries.length > 1 && <LineTabs summaries={summaries} spec={spec} outcome={target.outcome} lineValue={model?.lineValue ?? null} onNavigate={onNavigate} />}

      {model && !event?.live && (
        <LineHistoryRow target={target} spec={spec} event={event} entityName={entityName} model={model} modelAt={modelAt} bookStake={bookStake} isMobile={isMobile} onOpenHistory={onOpenHistory} openFor={openHistoryFor} />
      )}
      <div className="scrollbar-custom scrollbar-color-primary min-h-0 flex-grow overflow-y-auto px-4 pb-3 pt-3">
        {loading ? (
          <div className="py-10 text-center text-[12px] text-zinc-500">Loading books…</div>
        ) : model && model.columns.length > 0 ? (
          <RailColumns columns={model.columns} opens={opens} bookStake={bookStake} highlightOutcome={target.outcome} hasAccount={hasAccount} onLinkClick={onLinkClick} isMobile={isMobile} />
        ) : (
          <div className="py-10 text-center text-[12px] text-zinc-500">No books at this line.</div>
        )}
      </div>
    </aside>
  );
}
